/**
 * Hosts allowed to be embedded in an <iframe> by CMS `iframe` blocks.
 *
 * This list is the single source of truth for two things:
 *  - the `frame-src` directive of the Content-Security-Policy (next.config.js)
 *  - the runtime check in the portable-text `iframe` renderer (lib/portableText.js)
 *
 * It was derived from the 224 iframe embeds that exist in the production
 * dataset, minus two that were deliberately left out:
 *  - tpc.googlesyndication.com (an ad-serving host, not a media embed)
 *  - launch.newsinc.com (News Distribution Network, defunct — a dead domain
 *    is a takeover risk, so it should not be trusted to run in a frame)
 *
 * Matching is on hostname only, so existing http:// embeds behave exactly as
 * they do today (blocked by the browser as mixed content, not by this list).
 *
 * CommonJS so that next.config.js can require it.
 */

const EMBED_HOSTS = [
  // Video
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'www.dailymotion.com',
  'link.brightcove.com',
  'players.brightcove.net',
  'www.kaltura.com',
  'rudo.video',
  'videos.domainepublic.net',
  'www.wat.tv',
  'static01.nyt.com',
  // Audio / podcasts
  'w.soundcloud.com',
  'html5-player.libsyn.com',
  'open.spotify.com',
  'anchor.fm',
  'castbox.fm',
  'embed.acast.com',
  'embeds.audioboom.com',
  'embed.podcasts.apple.com',
  'www.ivoox.com',
  // Broadcasters
  'emp.bbc.co.uk',
  'www.cbc.ca',
  'www.rts.ch',
  'tp.srgssr.ch',
  'www.franceculture.fr',
  'player.canalplus.fr',
  // Documents, slides, images, forms
  'docs.google.com',
  'www.slideshare.net',
  'embed-ssl.ted.com',
  'www.flickr.com',
  'magic.piktochart.com',
  'embed.readymag.com',
  'www.surveymonkey.com',
  // Institutional / social
  'www.un.org',
  'www.facebook.com',
]

/**
 * True if `src` is an http(s) URL whose host is on the embed allowlist.
 * Anything unparseable, relative, off-list, or using another scheme is
 * rejected — the scheme check matters because the host of something like
 * `ftp://www.youtube.com/x` would otherwise pass.
 */
function isAllowedEmbed(src) {
  if (typeof src !== 'string' || !src) {
    return false
  }

  let url
  try {
    url = new URL(src)
  } catch (e) {
    return false
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false
  }

  return EMBED_HOSTS.includes(url.hostname.toLowerCase())
}

/**
 * Returns an allowlisted embed URL forced to https, or null if the embed is
 * not one we will render.
 *
 * Roughly half the embeds in the dataset were authored as http:// years ago.
 * Browsers block those on an https page as mixed content, so they render as
 * empty boxes today. Upgrading the scheme at render time fixes that without
 * rewriting the documents, and it cannot make anything worse: a host that
 * refuses https simply stays broken exactly as it is now.
 */
function toSecureEmbedUrl(src) {
  if (!isAllowedEmbed(src)) {
    return null
  }

  const url = new URL(src)
  url.protocol = 'https:'
  return url.toString()
}

module.exports = { EMBED_HOSTS, isAllowedEmbed, toSecureEmbedUrl }
